/**
* Sworn Translator JSON-LD @graph — LegalService + WebPage + FAQPage + HowTo
* + Service/Offer + BreadcrumbList. Adds Country/GovernmentService for
* embassy-specific pages so Google picks up the visa/legalization intent.
*/
import type { SwornEmbassy, SwornDocType } from "@/data/sworn";
import type { SwornPageContent } from "./generator";
import { SITE } from "@/config/sites";

export function buildSwornGraph(args: {
  url: string;
  title: string;
  description: string;
  content: SwornPageContent;
  embassy?: SwornEmbassy;
  doc?: SwornDocType;
  breadcrumb: Array<{ name: string; url: string }>;
}) {
  const host = SITE.canonicalHost ? `https://${SITE.canonicalHost}` : "";
  const org = {
    "@type": "LegalService",
    "@id": `${host}/#org`,
    name: SITE.name,
    url: host || undefined,
    areaServed: "TH",
    additionalType: "https://schema.org/ProfessionalService",
  };
  const webPage = {
    "@type": "WebPage",
    "@id": args.url, url: args.url,
    name: args.title, description: args.description,
    inLanguage: "th-TH",
    isPartOf: { "@id": org["@id"] },
    speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", "h3", ".speakable-summary", ".lead", ".answer-summary", ".faq-answer", "[data-speakable]"] },
  };
  const faq = {
    "@type": "FAQPage",
    mainEntity: args.content.faqs.map((f) => ({
      "@type": "Question", name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const service = args.doc ? {
    "@type": "Service",
    name: `Sworn Translation — ${args.doc.nameEn}${args.embassy ? ` (${args.embassy.nameEn})` : ""}`,
    serviceType: "Sworn / Embassy-certified translation",
    provider: { "@id": org["@id"] },
    areaServed: args.embassy ? args.embassy.nameEn : "Thailand",
  } : null;
  const embassyNode = args.embassy ? {
    "@type": "GovernmentService",
    name: `${args.embassy.nameEn} — Sworn Translation`,
    serviceOperator: { "@type": "GovernmentOrganization", name: args.embassy.nameEn },
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: args.url,
      availableLanguage: [args.embassy.language, "Thai", "English"],
    },
  } : null;
  const howTo = {
    "@type": "HowTo",
    name: `ขั้นตอนแปลสาบานตน${args.embassy ? ` ${args.embassy.nameTh}` : ""}${args.doc ? ` — ${args.doc.nameTh}` : ""}`,
    step: args.content.steps.map((s, i) => ({
      "@type": "HowToStep", position: i + 1, name: s.tag, text: s.text,
    })),
  };
  const breadcrumb = {
    "@type": "BreadcrumbList",
    itemListElement: args.breadcrumb.map((b, i) => ({
      "@type": "ListItem", position: i + 1, name: b.name, item: b.url,
    })),
  };
  return {
    "@context": "https://schema.org",
    "@graph": [org, webPage, faq, ...(service ? [service] : []), ...(embassyNode ? [embassyNode] : []), howTo, breadcrumb],
  };
}
